import { IS_PREVIEW } from "../lib/theme";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ListOrdered, CalendarDays, BarChart3, Users, ClipboardList, Briefcase, Award,
    FileText, ArrowLeftRight, Repeat, UserPlus, TrendingUp, Atom, PieChart, Trophy,
    Flag, MapPin, Link2, BookOpen, Newspaper, Video, MessageCircle, Map, Globe, ShieldCheck,
} from "lucide-react";
import { PWHL_SECTIONS, NHL_SECTIONS, NHL_TAB_ROUTES, NAT_TAB_ROUTES } from "./Layout";
import { useSortByTeam } from "../lib/SortByTeamContext";
import { useLeague } from "../context/LeagueContext";
import { getTeams, api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

// Per-page symbols — the Pages panel is icon-first (never word-only).
export const TAB_ICON = {
    standings: ListOrdered, schedule: CalendarDays, leaderboards: BarChart3,
    players: Users, roster: ClipboardList, staff: Briefcase, ins_awards: Award,
    contracts: FileText, transfers: ArrowLeftRight, trades: Repeat, draft: UserPlus,
    ins_rankings: TrendingUp, ins_chemistry: Atom, demographics: PieChart,
    ins_records: Trophy, ins_milestones: Flag, ins_positions: MapPin, ins_links: Link2,
    ins_stories: BookOpen, news: Newspaper, videos: Video, chat: MessageCircle,
};

// Mobile "Pages" panel — mirrors the DESKTOP TabChip exactly: small symbol+name
// pills, black-and-white by default, tinted with the SELECTED TEAM's colour when
// active (falls back to white). Overview pages are excluded (not navigable).
export function MobileNavBento({ onNavigate, accessLinks = [], onlyGroup = null, iconsOnly = false, inboxCount = 0 }) {
    const location = useLocation();
    const { surfTeam } = useSortByTeam();
    const { league } = useLeague();
    const isNhl = league === "nhl";
    const isNat = league === "international";
    const sections = isNhl ? NHL_SECTIONS : PWHL_SECTIONS;
    const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams, staleTime: 5 * 60 * 1000 });
    const { hasAdminAccess, isOwner } = useAuth();
    const { data: boardOpen } = useQuery({ queryKey: ["roadmap-alerts", "open-counts"], queryFn: () => api.get("/admin/roadmap/open-counts").then((r) => r.data), enabled: !!(hasAdminAccess || isOwner) && !IS_PREVIEW, staleTime: 30 * 1000 });
    const go = () => onNavigate && onNavigate();
    const isActive = (to) => location.pathname === (to || "").split("?")[0];
    const accent = surfTeam ? (teams.find((t) => t.id === surfTeam)?.primary_color || "#9D4CDD") : "#FFFFFF";

    const Chip = ({ to, label, Icon, testId, badge = 0 }) => {
        const active = isActive(to);
        if (iconsOnly) {
            return (
                <Link to={to} onClick={go} data-testid={testId} title={label} aria-label={label}
                    style={active ? { background: `${accent}26`, borderColor: `${accent}59` } : undefined}
                    className={`relative inline-flex items-center justify-center h-12 w-12 rounded-xl border transition-all ${active ? "text-white" : "border-white/10 text-[#94A3B8] active:text-white active:border-white/25"}`}>
                    <Icon className="h-5 w-5 shrink-0" strokeWidth={2} style={active ? { color: accent } : undefined} />
                    {badge > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EF4444] text-white text-[8px] font-black flex items-center justify-center leading-none" data-testid="mobile-bento-inbox-badge">+{badge}</span>}
                </Link>
            );
        }
        return (
            <Link to={to} onClick={go} data-testid={testId} title={label}
                style={active ? { background: `${accent}26`, borderColor: `${accent}59` } : undefined}
                className={`group relative flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-all ${active ? "text-white" : "border-white/10 text-[#94A3B8] active:text-white active:border-white/25"}`}>
                <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} style={active ? { color: accent } : undefined} />
                <span className="text-[9px] font-bold uppercase tracking-wider leading-none whitespace-nowrap">{label}</span>
                {badge > 0 && <span className="ml-1 min-w-[15px] h-[15px] px-1 rounded-full bg-[#EF4444] text-white text-[8px] font-black inline-flex items-center justify-center leading-none">+{badge}</span>}
            </Link>
        );
    };
    // NHL greys out any tab with no live NHL route — mirrors the desktop nav exactly.
    const GreyChip = ({ label, Icon, testId }) => (
        <span data-testid={testId} title={`${label} — coming soon for NHL`}
            className="group flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 text-[#4B5563] cursor-not-allowed select-none">
            <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            <span className="text-[9px] font-bold uppercase tracking-wider leading-none whitespace-nowrap">{label}</span>
        </span>
    );
    const renderTab = (t) => {
        const Icon = TAB_ICON[t.id] || Globe;
        const badge = boardOpen && ["roadmap", "bugs", "inbox"].includes(t.id) ? (boardOpen[t.id] || 0) : t.id === "inbox" ? inboxCount : 0;
        if (isNhl || isNat) {
            const route = (isNat ? NAT_TAB_ROUTES : NHL_TAB_ROUTES)[t.id];
            if (!route) return <GreyChip key={t.id} label={t.l} Icon={Icon} testId={`bento-tile-${t.id}`} />;
            return <Chip key={t.id} to={route} label={t.l} Icon={Icon} testId={`bento-tile-${t.id}`} badge={badge} />;
        }
        return <Chip key={t.id} to={t.to} label={t.l} Icon={Icon} testId={`bento-tile-${t.id}`} badge={badge} />;
    };

    if (onlyGroup) {
        const sec = sections.find((s) => s.key === onlyGroup) || sections[0];
        return (
            <div className="mx-auto w-full max-w-md" data-testid="mobile-bento">
                <div className="flex items-center justify-center gap-1.5 text-white/40 mb-3">
                    {sec.Icon && <sec.Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />}
                    <span className="text-[10px] font-black uppercase tracking-[0.22em]">{sec.title}</span>
                </div>
                <div className="flex flex-wrap gap-2.5 justify-center">
                    {(sec.tabs || []).map(renderTab)}
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full max-w-md space-y-3" data-testid="mobile-bento">
            {accessLinks.length > 0 && (
                <div data-testid="bento-section-access">
                    <div className="flex items-center justify-center gap-1 text-white/35 mb-1.5">
                        <ShieldCheck className="h-3 w-3 shrink-0" strokeWidth={2} />
                        <span className="text-[9px] font-black uppercase tracking-[0.22em]">Access</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                        {accessLinks.map((a) => {
                            const active = isActive(a.to);
                            const AIcon = a.Icon || Globe;
                            return (
                                <Link key={a.key} to={a.to} onClick={go} data-testid={`bento-access-${a.key}`} title={a.label}
                                    style={active ? { background: `${a.color}26`, borderColor: `${a.color}80`, color: a.color } : { borderColor: `${a.color}66`, color: a.color }}
                                    className="group flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-all active:brightness-125">
                                    <AIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                                    <span className="text-[9px] font-bold uppercase tracking-wider leading-none whitespace-nowrap">{a.label}</span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
            {sections.map((s) => {
                if (!(s.tabs && s.tabs.length)) {
                    return (
                        <div key={s.key} className="flex flex-wrap gap-1.5 justify-center" data-testid={`bento-section-${s.key}`}>
                            <Chip to={s.to || "/"} label={s.title} Icon={s.Icon || Map} testId={`bento-tile-${s.key}`} />
                        </div>
                    );
                }
                return (
                    <div key={s.key} data-testid={`bento-section-${s.key}`}>
                        <div className="flex items-center justify-center gap-1 text-white/35 mb-1.5">
                            {s.Icon && <s.Icon className="h-3 w-3 shrink-0" strokeWidth={2} />}
                            <span className="text-[9px] font-black uppercase tracking-[0.22em]">{s.title}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 justify-center">
                            {s.tabs.map(renderTab)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
